import React, { useMemo, useState, Key } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useQueryParams } from 'use-query-params'

import { ContentLayout } from '@app/layouts'
import { ExtendedTable, PermissionWrapper, SubheaderToolbarDivider, TableSettingsToolbar } from '@app/components'
import { LntButton, LntDropdown } from '@app/components/base'
import { Icon, Show } from '@app/components/util'
import { getFiltersDataFromQuotaReportTabFilters, getQuotaReportsDataFromResponse } from '@app/utils/responseAdapters'
import { exportQuotaReportFile, exportQuotaReportsFile } from '@app/utils/export'
import { getExportFilesDropdownMenu } from '@app/utils'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { QUOTAS_REPORTS_KEY } from '@app/constants/queryKeys'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { FILE_TYPES } from '@app/api/constants'
import { PERMISSION } from '@app/constants/permission'
import { ICON_NAME } from '@app/constants/icon'

import { usePaginationParams, useTableSearch, useTableSetting, useToggle } from '@app/hooks'

import { getQuotaReports } from '@app/api/routes/quotaReports'

import { TopFilters } from './ui/TopFilters'
import { getColumns } from './lib/utils'
import { topFiltersQuerySchema, columnsSearchSchema, tableSearchQuerySchema } from './configs'

import type { ReactNode } from 'react'
import type { FiltersParams } from '@app/types/filter'
import type { GetQuotaParams } from '@app/api/routes/quotaReports/types'
import type { FileType } from '@app/types'

const columns = getColumns()

export const QuotaReportTab: React.FC = () => {
  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)

  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationParams()

  const quotaReportsParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetQuotaParams

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const buttonLoadingExportToggle = useToggle()
  const buttonLoadingExportAllToggle = useToggle()

  const { data: quotaReportsData, isLoading: isQuotaReportsDataLoading } = useQuery({
    queryKey: [QUOTAS_REPORTS_KEY.quotaReportItems, quotaReportsParams],
    queryFn: async () => await getQuotaReports(quotaReportsParams)
  })

  const handleExportQuotaReportFile = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportToggle.setToggled()

    const fileName = 'Отчет_по_квоте'

    try {
      await exportQuotaReportFile(fileName, { ids: selectedIds, file: fileType })
    } finally {
      buttonLoadingExportToggle.setUntoggled()
    }
  }

  const handleExportQuotaReportsFile = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportAllToggle.setToggled()

    const data = {
      ...filtersData,
      file: fileType
    }

    const fileName = 'Отчет_по_квотам'

    try {
      await exportQuotaReportsFile(fileName, data)
    } finally {
      buttonLoadingExportAllToggle.setUntoggled()
    }
  }

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys.map(String))
  }

  const filtersData = useMemo(() => getFiltersDataFromQuotaReportTabFilters(queryFilters), [queryFilters])

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.quotaReportTab, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredQuotaReportsDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: getQuotaReportsDataFromResponse(quotaReportsData),
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Отчет по квоте',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportQuotaReportFile
  )
  const exportAllDropdownItems = getExportFilesDropdownMenu(
    'Отчет по квотам',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportQuotaReportsFile
  )

  const toolbar: ReactNode[] = [
    <Show
      key='view_export-show'
      condition={ selectedIds.length === 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.viewQuotas] }>
        <LntDropdown
          trigger={ ['click'] }
          items={ exportDropdownItems }
        >
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            loading={ buttonLoadingExportToggle.value }
          >
            Экспорт
            <Icon name={ ICON_NAME.arrowDown } />
          </LntButton>
        </LntDropdown>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <PermissionWrapper
      key='export_all-button'
      requiredPermissions={ [PERMISSION.viewQuotas] }
    >
      <LntDropdown
        trigger={ ['click'] }
        items={ exportAllDropdownItems }
      >
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          loading={ buttonLoadingExportAllToggle.value }
        >
          Экспорт всех
          <Icon name={ ICON_NAME.arrowDown } />
        </LntButton>
      </LntDropdown>

      <SubheaderToolbarDivider />
    </PermissionWrapper>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <ContentLayout>
      <TopFilters
        onSearch={ handleFiltersChange }
        initialFilters={ queryFilters as FiltersParams }
      />

      <ExtendedTable
        loading={ isQuotaReportsDataLoading }
        columns={ columnsWithSearchTool }
        dataSource={ filteredQuotaReportsDataSource }
        toolbar={ toolbar }
        rowSelection={ {
          onChange: handleSelectedRowChange
        } }
        pagination={ {
          ...pagination,
          total: quotaReportsData?.count,
          onChange: handlePaginationChange
        } }
      />
    </ContentLayout>
  )
}
